import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getNavItems } from '../../config/navigation';
import { meetingNotesApi } from '../../lib/api';

export default function Sidebar({ currentPage, onNavigate, mobileOpen, onCloseMobile }) {
  const { role, profile, hasSubscription, hasEventAccess, subscriptionTier, signOut } = useAuth();
  const [notesBadge, setNotesBadge] = useState(0);

  useEffect(() => {
    if (!hasSubscription && role !== 'admin') return;
    meetingNotesApi.getInsights()
      .then(res => {
        const unread = (res.insights || []).filter(i => !i.is_read).length;
        setNotesBadge(unread);
      })
      .catch(() => {});
  }, [hasSubscription, role]);

  const items = getNavItems(role, hasSubscription, hasEventAccess, subscriptionTier, { notes: notesBadge });

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={onCloseMobile} />
      )}

      <aside
        className={`fixed top-0 left-0 h-full w-[220px] bg-white border-r border-vault-border z-50 transform transition-transform duration-200 lg:translate-x-0 flex flex-col ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="px-5 py-5 border-b border-vault-border">
          <img src="/logo-playventr.png" alt="PlayVentr" className="h-7" />
        </div>

        {/* Nav */}
        <nav className="flex-1 mt-1 px-2 overflow-y-auto">
          {items.map((item, idx) => {
            if (item.section) {
              return (
                <div key={item.section} className={`px-3 ${idx > 0 ? 'mt-4' : 'mt-2'} mb-1`}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-vault-gold">{item.section}</div>
                  {item.hint && <div className="text-[10px] text-gray-400 mt-0.5">{item.hint}</div>}
                </div>
              );
            }
            const isActive = currentPage === item.key;
            const isLocked = item.locked;
            return (
              <button
                key={item.key}
                onClick={() => {
                  onNavigate(item.key);
                  onCloseMobile();
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[14px] font-medium mb-0.5 transition-all relative ${
                  isActive
                    ? 'bg-amber-50 text-vault-gold'
                    : isLocked
                    ? 'text-gray-300 hover:bg-gray-50 hover:text-gray-400'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                }`}
              >
                {isActive && !isLocked && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-vault-gold rounded-r-full" />
                )}
                <span className={`text-[16px] w-5 text-center shrink-0 ${isLocked ? 'opacity-40' : ''}`}>{item.icon}</span>
                <span className={`truncate ${isLocked ? 'opacity-50' : ''}`}>{item.label}</span>
                {isLocked && (
                  <svg className="ml-auto w-3.5 h-3.5 text-gray-300 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                )}
                {!isLocked && item.badge > 0 && (
                  <span className="ml-auto w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="px-3 py-3 border-t border-vault-border">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-vault-gold to-amber-500 flex items-center justify-center text-white font-semibold text-xs shrink-0">
              {profile?.display_name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-vault-navy truncate">{profile?.display_name}</div>
              <div className="text-[11px] text-gray-400 capitalize">
                {role === 'admin' ? 'Admin'
                  : role === 'inventor' ? 'Inventor'
                  : role?.replace(/_/g, ' ')}
              </div>
            </div>
          </div>
          <button
            onClick={signOut}
            className="w-full text-left px-3 py-1.5 text-[12px] text-gray-400 hover:text-vault-error transition-colors rounded"
          >
            Sign out
          </button>
        </div>
      </aside>
    </>
  );
}
